import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

/* ══════════════════════════════════════════════
 * PUBLIC QUERIES
 * ══════════════════════════════════════════════ */

/**
 * Fetch all public team members ordered by order_index.
 */
export function usePublicTeam() {
  return useQuery({
    queryKey: ['team'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('team_members')
        .select('id, full_name, slug, role, tagline, image_path, phone, whatsapp, email, social_links, is_featured_on_home, order_index')
        .eq('is_public', true)
        .order('order_index', { ascending: true });
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Fetch featured team members for homepage strip.
 */
export function useFeaturedTeam() {
  return useQuery({
    queryKey: ['team', 'featured'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('team_members')
        .select('id, full_name, slug, role, tagline, image_path, phone, whatsapp, social_links, order_index')
        .eq('is_public', true)
        .eq('is_featured_on_home', true)
        .order('order_index', { ascending: true });
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Fetch full team member detail by id or slug.
 */
export function useTeamMember(idOrSlug) {
  return useQuery({
    queryKey: ['team', idOrSlug],
    queryFn: async () => {
      // Try by id first, then slug
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(idOrSlug);
      const column = isUUID ? 'id' : 'slug';
      const { data, error } = await supabase
        .from('team_members')
        .select('*')
        .eq(column, idOrSlug)
        .eq('is_public', true)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!idOrSlug,
    staleTime: 1000 * 60 * 5,
  });
}

/* ══════════════════════════════════════════════
 * ADMIN QUERIES
 * ══════════════════════════════════════════════ */

/**
 * Fetch all team members for admin (including hidden).
 */
export function useAdminTeam() {
  return useQuery({
    queryKey: ['admin', 'team'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('team_members')
        .select('*')
        .order('order_index', { ascending: true });
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 2,
  });
}

/* ══════════════════════════════════════════════
 * ADMIN MUTATIONS
 * ══════════════════════════════════════════════ */

/**
 * Create a new team member.
 */
export function useCreateTeamMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const { data, error } = await supabase
        .from('team_members')
        .insert(payload)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'team'] });
      queryClient.invalidateQueries({ queryKey: ['team'] });
    },
  });
}

/**
 * Update an existing team member.
 */
export function useUpdateTeamMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }) => {
      const { data, error } = await supabase
        .from('team_members')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'team'] });
      queryClient.invalidateQueries({ queryKey: ['team'] });
    },
  });
}

/**
 * Delete a team member.
 */
export function useDeleteTeamMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase
        .from('team_members')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return { id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'team'] });
      queryClient.invalidateQueries({ queryKey: ['team'] });
    },
  });
}

/**
 * Batch reorder team members.
 */
export function useReorderTeamMembers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (orderedIds) => {
      const updates = orderedIds.map((id, index) =>
        supabase.from('team_members').update({ order_index: index + 1 }).eq('id', id)
      );
      const results = await Promise.all(updates);
      const failed = results.find((r) => r.error);
      if (failed) throw failed.error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'team'] });
      queryClient.invalidateQueries({ queryKey: ['team'] });
    },
  });
}

/**
 * Toggle is_public for a team member (optimistic).
 */
export function useToggleTeamVisibility() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, is_public }) => {
      const { error } = await supabase
        .from('team_members')
        .update({ is_public, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
      return { id, is_public };
    },
    onMutate: async ({ id, is_public }) => {
      await queryClient.cancelQueries({ queryKey: ['admin', 'team'] });
      const prev = queryClient.getQueryData(['admin', 'team']);
      queryClient.setQueryData(['admin', 'team'], (old) =>
        (old || []).map((m) => (m.id === id ? { ...m, is_public } : m))
      );
      return { prev };
    },
    onError: (_, __, context) => {
      queryClient.setQueryData(['admin', 'team'], context.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'team'] });
      queryClient.invalidateQueries({ queryKey: ['team'] });
    },
  });
}

/**
 * Toggle is_featured_on_home for a team member (optimistic).
 */
export function useToggleTeamFeatured() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, is_featured_on_home }) => {
      const { error } = await supabase
        .from('team_members')
        .update({ is_featured_on_home, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
      return { id, is_featured_on_home };
    },
    onMutate: async ({ id, is_featured_on_home }) => {
      await queryClient.cancelQueries({ queryKey: ['admin', 'team'] });
      const prev = queryClient.getQueryData(['admin', 'team']);
      queryClient.setQueryData(['admin', 'team'], (old) =>
        (old || []).map((m) => (m.id === id ? { ...m, is_featured_on_home } : m))
      );
      return { prev };
    },
    onError: (_, __, context) => {
      queryClient.setQueryData(['admin', 'team'], context.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'team'] });
      queryClient.invalidateQueries({ queryKey: ['team'] });
    },
  });
}

/**
 * Upload team member image to Supabase Storage.
 */
export async function uploadTeamImage(file, memberId) {
  const ext = file.name.split('.').pop();
  const path = `team/${memberId}/${Date.now()}.${ext}`;
  const { error } = await supabase.storage.from('images').upload(path, file, {
    cacheControl: '3600',
    upsert: false,
  });
  if (error) throw error;
  return path;
}
